export interface TierPricing {
  price: string
  unit?: string
  tagline: string
}

// Normalizes feature / tier names so overrides survive small wording changes
export function normalizeKey(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '')
}

// Display names for license columns whose CSV header differs from the brand name
export const tierDisplayNames: {
  [key: string]: string
} = {
  max: 'Ultra',
}

// Corrections applied on top of the pricing CSV, keyed by feature then license.
// Feature keys match partially, so "fleetwifisharing" also covers
// "Fleet Wi-Fi Sharing (Enterprise)".
export const featureValueOverrides: {
  [feature: string]: {
    [tier: string]: string
  }
} = {
  fleetwifisharing: {
    pro: '—',
  },
}

// CSV rows to drop, matched loosely on the feature name.
export const hiddenFeatureKeys: string[] = []

export function isHiddenFeature(feature: string) {
  const featureKey = normalizeKey(feature)
  return hiddenFeatureKeys.some((key) => featureKey.includes(key))
}

// Rows missing from the CSV, prepended to the matching category.
export interface AdditionalFeatureGroup {
  categoryMatch: string
  categoryLabel: string
  features: {
    feature: string
    description: string
  }[]
}

export const additionalFeatureGroups: AdditionalFeatureGroup[] = [
  {
    categoryMatch: 'collaborate',
    categoryLabel: 'Ari Collaborate',
    features: [
      {
        feature: 'Incoming calls',
        description: 'Answer a call hands-free, straight on the headset.',
      },
      {
        feature: 'Outgoing calls',
        description: 'Start a call with any colleague by voice.',
      },
      {
        feature: 'Contact search',
        description: 'Find the right person by name, no scrolling required.',
      },
      {
        feature: 'Groups',
        description: 'Reach an entire team at once from a saved group.',
      },
      {
        feature: 'Calendar',
        description: 'See scheduled meetings and join them in a single step.',
      },
      {
        feature: 'Recent calls',
        description: 'Pick up where you left off with recent contacts.',
      },
      {
        feature: 'Collaborate plugin',
        description: 'Bring Collaborate into the tools your teams already use.',
      },
      {
        feature: 'Thermal camera',
        description: 'Stream thermal imagery into the call on supported devices.',
      },
      {
        feature: 'Expert screen sharing',
        description: 'Remote experts share their screen into your field of view.',
      },
      {
        feature: 'Photo capture',
        description: 'Take a still image and send it during the call.',
      },
    ],
  },
]

export function findFeatureOverrides(feature: string) {
  const featureKey = normalizeKey(feature)
  const match = Object.keys(featureValueOverrides).find((key) =>
    featureKey.includes(key),
  )
  return match ? featureValueOverrides[match] : undefined
}

// Keyed by the license name used in the pricing CSV (lowercased)
export const tierPricing: {
  [key: string]: TierPricing
} = {
  core: {
    price: 'Free',
    tagline: 'Everything you need to work hands-free.',
  },
  pro: {
    price: '$9.90',
    unit: 'per device / month',
    tagline: 'Deeper AI, more cloud, more control.',
  },
  ultra: {
    price: '$19.90',
    unit: 'per device / month',
    tagline: 'The complete platform, at fleet scale.',
  },
}
